import { Injectable } from '@angular/core';
import { JobFile } from '@core/models/job-file';
import { User } from '@core/models/user.model';
import { SetNotificationAction } from '@core/store/notification/notification.actions';
import { SetSkeletonLoader, SetSpinner } from '@core/store/shared.actions';
import { Action, Actions, ofActionCompleted, Selector, State, StateContext, Store } from '@ngxs/store';
import { AlertDialogType } from '@shared/models';
import { EntityMap } from '@shared/models/types';
import { FileHandlerService } from '@shared/services/file-handler.service';
import {catchError, map, mergeMap, switchMap, tap} from 'rxjs/operators';
import { of } from 'rxjs';
import { JobGroupPagination } from '../models/job-group-pagination.model';
import { JobGroup } from '../models/job-group.model';
import { JobGroupStateModel } from '../models/state/job-group-state.model';
import { JobGroupService } from '../services/job-group.service';
import { CopyLoadedJobsGroupsDataToJobFileCreationJobGroupsData, CreateJobFile, CreateJobGroup,
  FetchJobFiles, FetchJobGroups, FetchInternalCollaborators, SearchJobGroupsForJobFileCreation,
  SelectJobGroup, UpdateJobGroup, NavigateToJobFile, ProcessOrgLogoUrl, FetchJobFileStatus,
  NavigateToModule, MoveJobFileToGroup, TickJobFile, GetWorkOrderCompletionPercentage, FetchJobFilesByGroupId, SetJobGroupSearchKey, 
  SetSelectedJobGroupForDashboardAfterCreation, DeleteJobGroup, GetJpmIdsByJobRefId} from './job-group.actions';
import { JobMasterService } from 'app/modules/reporting/services/job-master.service';
import {getJobFilePaginationRequest} from '@schedule/test-data';
import { throwError } from 'rxjs';
import { Jpm } from '@control-tower/models/jpm';

export const jobGroupStateKey = 'jobGroup';
export const getInitialJobGroupState = (): JobGroupStateModel => (
  {
    selectedJobGroupId: null,
    filteredJobGroupId: null,
    jobGroups: {},
    jobGroupPaginationData: {
      pageNumber: 0,
      pageSize: 10,
      totalElements: 0
    },
    // job files
    jobFiles: {},
    jobFilesLoading: false,
    tickedJobFiles: {},
    jobFilePaginationData: {
      pageNumber: 0,
      pageSize: 10,
      totalElements: 0
    },
    logos: {},
    // job file creation
    jobGroupsForJobFileCreation: {},
    selectedJobGroupIdForJobFileCreation: null,
    internalCollaborators: [],
    loadPercentage: {},
    searchKey: '',
    selectedJobGroupIdForDashboardAfterCreation: '',
    selectedJobGroupForDashboardAfterCreation: null,
    jpmIds: []
  });
@State({
  name: jobGroupStateKey,
  defaults: getInitialJobGroupState()
})
@Injectable()
export class JobGroupState {
  constructor(private store: Store,
    private jobGroupService: JobGroupService,
    private fileHandlerService: FileHandlerService,
    private jobMasterService: JobMasterService,
    private actions$: Actions,
  ) {}

  @Selector()
  static getSelectedJobGroupId(state: JobGroupStateModel): string {
    return state.selectedJobGroupId;
  }

  @Selector()
  static getFilteredJobGroupId(state: JobGroupStateModel): string {
    return state.filteredJobGroupId;
  }

  @Selector()
  static getSelectedJobGroupIdForJobFileCreation(state: JobGroupStateModel): string {
    return state.selectedJobGroupIdForJobFileCreation;
  }

  @Selector()
  static getSelectedJobGroup(state: JobGroupStateModel): JobGroup {
    if (!state.selectedJobGroupId) {
      return null;
    }
    return state.jobGroups[state.selectedJobGroupId];
  }

  @Selector()
  static getSelectedJobGroupForJobFileCreation(state: JobGroupStateModel): JobGroup {
    if (!state.selectedJobGroupIdForJobFileCreation) {
      return null;
    }
    return state.jobGroupsForJobFileCreation[state.selectedJobGroupIdForJobFileCreation];
  }

  @Selector()
  static getJobGroups(state: JobGroupStateModel): JobGroup[] {
    return Object.values(state.jobGroups);
  }

  @Selector()
  static getJobGroupsForJobFileCreation(state: JobGroupStateModel): JobGroup[] {
    return Object.values(state.jobGroupsForJobFileCreation).sort((a, b) => {
      // Keep 'default' job group at the end
      if (a.id === 'default') return 1;
      if (b.id === 'default') return -1;
      
      // Sort by createdTimestamp descending (newest first)
      // If createdTimestamp is missing, treat as oldest (put before default)
      const aTime = a.createdTimestamp ? new Date(a.createdTimestamp).getTime() : 0;
      const bTime = b.createdTimestamp ? new Date(b.createdTimestamp).getTime() : 0;
      return bTime - aTime;
    });
  }

  @Selector()
  static getJobFiles(state: JobGroupStateModel): JobFile[] {
    return Object.values(state.jobFiles);
  }

  @Selector()
  static getLogosMap(state: JobGroupStateModel): EntityMap<string, string> {
    return state.logos;
  }

  @Selector()
  static getJobGroupPaginationData(state: JobGroupStateModel): JobGroupPagination {
    return state.jobGroupPaginationData;
  }

  @Selector()
  static getJobFilePaginationData(state: JobGroupStateModel): JobGroupPagination {
    return state.jobFilePaginationData;
  }

  @Selector()
  static getJobFileInternalCollaborator(state: JobGroupStateModel): User[] {
    return state.internalCollaborators;
  }

  @Selector()
  static getLoadPercentage(state: JobGroupStateModel): EntityMap<string, number> {
    return state.loadPercentage;
  }

  @Selector()
  static getSelectedJobGroupForDashboardAfterCreation(state: JobGroupStateModel): JobGroup {
    return state.selectedJobGroupForDashboardAfterCreation;
  }

  @Selector()
  static getSelectedJobGroupIdForDashboardAfterCreation(state: JobGroupStateModel): string {
    return state.selectedJobGroupIdForDashboardAfterCreation;
  }

  @Selector()
  static getJpmIds(state: JobGroupStateModel): Jpm[] {
    return state.jpmIds;
  }

  @Action(SelectJobGroup)
  selectJobGroupId({ patchState }: StateContext<JobGroupStateModel>, {id}: SelectJobGroup) {
    return patchState({
      selectedJobGroupId: id,
      jobFiles: {},
      jobFilesLoading: true,
      jobFilePaginationData: {
        pageNumber: 0,
        pageSize: 10,
        totalElements: 0
      }
    });
  }

  @Action(CreateJobGroup)
  createJobGroup({dispatch, patchState, getState}: StateContext<JobGroupStateModel>, {name}: CreateJobGroup) {
    const {user: {orgId}} = this.store.selectSnapshot(state => state.auth);
    return this.jobGroupService.createJobGroup({
      orgId,
      name
    })
    .pipe(
      catchError((err) => dispatch(new SetNotificationAction('Job Group Creation Failed', AlertDialogType.ERROR))),
      tap( _ => {
        dispatch(new SetNotificationAction('Job Group Created Successfully', AlertDialogType.SUCCESS));
      }),
      // TODO: check weather we need to refresh or not
      mergeMap(() => dispatch(new FetchJobGroups()))
    );
  }

  @Action(UpdateJobGroup)
  updateJobGroup({ dispatch }: StateContext<JobGroupStateModel>, { jg }: UpdateJobGroup) {
    const {user: {orgId}} = this.store.selectSnapshot(state => state.auth);
    return this.jobGroupService.updateJobGroup(jg)
    .pipe(
      catchError((err) => dispatch(new SetNotificationAction('Job Group Update Failed', AlertDialogType.ERROR))),
      mergeMap(() => dispatch(new SetNotificationAction('Job Group Updated Successfully', AlertDialogType.SUCCESS))),
      mergeMap(() => dispatch(new FetchJobGroups()))
    );
  }

  @Action(FetchJobGroups)
  fetchJobGroups({ dispatch, patchState, getState }: StateContext<JobGroupStateModel>, { searchKey, pageNumber }: FetchJobGroups) {
    const {user: {orgId}} = this.store.selectSnapshot(state => state.auth);
    const {jobGroupPaginationData: {pageSize}} = getState();
    return this.jobGroupService.getJobGroups(orgId, pageNumber, pageSize, searchKey)
      .pipe(
        map((res) => {
          const firstNonDefaultGroup = res.content.find(group => group.id !== 'default');
          const filteredJobGroupId = firstNonDefaultGroup?.id || 'default';


          return patchState({
            jobGroupPaginationData: {
              pageNumber: res.pageable.pageNumber,
              pageSize: res.pageable.pageSize,
              totalElements: res.totalElements
            },
            jobGroups: res.content.reduce((prev, curr) => {
              prev[curr.id!=null ? curr.id : 'default'] = curr;
              return prev;
            }, {}),

            filteredJobGroupId: res.content[0].id,
            selectedJobGroupId: res.content[0].id
          });
        }),
        catchError((_) => dispatch(new SetNotificationAction('Job Group Loading Failed', AlertDialogType.ERROR))),
      );
  }


  @Action(FetchJobFiles)
  fetchJobFiles({ dispatch, patchState, getState }: StateContext<JobGroupStateModel>, { searchKey, pageNumber }: FetchJobFiles) {
    const {jobFilePaginationData: {pageSize}, selectedJobGroupId} = getState();

    const groupIdForQuery = searchKey && searchKey !== ''
        ? null
        : (selectedJobGroupId === 'default' ? null : selectedJobGroupId);
    return this.jobGroupService
        .getJobFiles(groupIdForQuery, pageNumber, pageSize, searchKey)
        .pipe(
            map((res) =>
            {
              dispatch(new SetSkeletonLoader(false));
              return patchState({
                    jobFilePaginationData: {
                        pageNumber: res.pageable.pageNumber,
                        pageSize: res.pageable.pageSize,
                        totalElements: res.totalElements
                    },
                    jobFilesLoading: false,
                    jobFiles: res.content.reduce((prev, curr) => {
                        prev[curr.jobRefId] = curr;
                        return prev;
                    }, {})
                });
            }),
            catchError((_) => dispatch(new SetNotificationAction('Job Files Loading Failed', AlertDialogType.ERROR))),
        );
  }

  @Action(FetchJobFilesByGroupId)
  fetchJobFilesByGroupId(
    { dispatch, patchState, getState}: StateContext<JobGroupStateModel>,
    { jobGroupId, searchKey, pageNumber, pageSize }: FetchJobFilesByGroupId
  ) {
    const state = getState();

    const groupIdForQuery = jobGroupId;
    return this.jobGroupService
      .getJobFiles(groupIdForQuery, pageNumber, pageSize, searchKey)
      .pipe(
        map((res) => {
          dispatch([new SetSkeletonLoader(false)]);
          return patchState({
            jobFilePaginationData: {
              pageNumber: res.pageable.pageNumber,
              pageSize: res.pageable.pageSize,
              totalElements: res.totalElements
            },
            jobFilesLoading: false,
            jobFiles: res.content.reduce((prev, curr) => {
              prev[curr.jobRefId] = curr;
              return prev;
            }, {})
          });
        }),
        catchError((_) => dispatch(new SetNotificationAction('Job Files Loading Failed', AlertDialogType.ERROR))),
      );
  }


  @Action(CopyLoadedJobsGroupsDataToJobFileCreationJobGroupsData)
  copyLoadedJobsGroupsToJobFileCreationJobGroupMap({getState, patchState}: StateContext<JobGroupStateModel>) {
    const {user: {orgId}} = this.store.selectSnapshot(state => state.auth);
    
    return this.jobGroupService.getJobGroups(orgId, 0, 100, '')
      .pipe(
        map((res) => patchState({
          jobGroupsForJobFileCreation: res.content.reduce((prev, curr) => {
            prev[curr.id != null ? curr.id : 'default'] = curr;
            return prev;
          }, {default: { name: 'DEFAULT', orgId, id: 'default'}}),
          selectedJobGroupIdForJobFileCreation: getState().selectedJobGroupId
        })),
        catchError((_) => {
          patchState({
            jobGroupsForJobFileCreation: {
              ...getState().jobGroups
            },
            selectedJobGroupIdForJobFileCreation: getState().selectedJobGroupId
          });
          return of(null);
        })
      );
  }

  @Action(SearchJobGroupsForJobFileCreation)
  searchJobGroupsForJobFileCreation({ dispatch, patchState, getState }: StateContext<JobGroupStateModel>, { searchText, selectFirstResult }: SearchJobGroupsForJobFileCreation) {
    const {user: {orgId}} = this.store.selectSnapshot(state => state.auth);

    return this.jobGroupService.getJobGroups(orgId, 0, 20, !!searchText ? searchText : '')
      .pipe(
        map((res) => patchState({
            jobGroupsForJobFileCreation: res.content.reduce((prev, curr) => {
              prev[curr.id] = curr;
              return prev;
            }, {default: { name: 'DEFAULT', orgId, id: 'default'}}),
            ...selectFirstResult && {selectedJobGroupIdForJobFileCreation: res.content[0].id}
          })),
        catchError((_) => dispatch(new SetNotificationAction('Job Group Loading Failed', AlertDialogType.ERROR))),
      );
  }

  @Action(FetchInternalCollaborators)
  loadInternalCollaborators({dispatch, patchState}: StateContext<JobGroupStateModel>, {scopes, searchText}: FetchInternalCollaborators) {
    const scopesForCollaborators = ['create:job-file', 'approve:job-file'];
    return this.jobGroupService.fetchUsersByScopes(
      scopes && scopes.length > 0 ? scopes : scopesForCollaborators,
      0,
      20,
      searchText
    )
      .pipe(
        map(res => patchState({
            internalCollaborators: res.content
          })),
        catchError((_) => dispatch(new SetNotificationAction('Internal Collaborators Loading Failed', AlertDialogType.ERROR)))
      );
  }

  @Action(CreateJobFile)
  createJobFile({ dispatch }: StateContext<JobGroupStateModel>, { jobFileCreationModel }: CreateJobFile) {
    const {user: {orgId}} = this.store.selectSnapshot(state => state.auth);
    return this.jobGroupService.createJobFile({...jobFileCreationModel, orgId})
      .pipe(
        mergeMap((res) => dispatch([
          new SetNotificationAction('Job File Created Successfully', AlertDialogType.SUCCESS),
          new NavigateToJobFile(res)
        ])),
        catchError((_) => dispatch(new SetNotificationAction('Job File Creation Failed', AlertDialogType.ERROR)))
      );
  }

  @Action(ProcessOrgLogoUrl)
  processOrgLogoUrl({ dispatch, getState, patchState }: StateContext<JobGroupStateModel>) {
    const { jobFiles } = getState();
    const orgIds = Array.from(new Set(Object.values(jobFiles)
      .filter(jf => jf.customer && jf.customer.orgId)
      .map(jf => jf.customer.orgId)));

    return this.jobGroupService.getOrLogsByOrgIds(orgIds)
      .pipe(
        mergeMap(logoIds => this.fileHandlerService.fetchFiles(logoIds)),
        map(res => patchState({
            logos: {
              ...getState().logos,
              ...res.reduce((prev, curr, i) => {
                prev[orgIds[i]] = curr.url;
                return prev;
              }, {})
            }
          })),
        catchError((_) => dispatch(new SetNotificationAction('Error loading Logos', AlertDialogType.ERROR)))
      );
  }

  @Action(FetchJobFileStatus)
  fetchJobFileStatus({ dispatch }: StateContext<JobGroupStateModel>, {taskFeedJob}: FetchJobFileStatus) {
    const {orgId, jobRefId} = taskFeedJob;
    return this.jobGroupService.getTaskFeedJobFromJobRefId(orgId, jobRefId)
      .pipe(
        mergeMap(res => dispatch([new SetSpinner(false), new NavigateToModule(res, taskFeedJob)])),
        catchError((_) => dispatch([new SetSpinner(false), new SetNotificationAction('Error loading Logos', AlertDialogType.ERROR)]))
      );
  }

  @Action(MoveJobFileToGroup)
  moveJobFileToGroup({dispatch, getState, patchState}: StateContext<JobGroupStateModel>, {jobRefId, newJobGroupId}: MoveJobFileToGroup) {
    const {user: {orgId}} = this.store.selectSnapshot(state => state.auth);
    const {jobGroupPaginationData: {pageNumber}, tickedJobFiles} = getState();
    const jobRefIdsSet = new Set(Object.keys(tickedJobFiles));
    jobRefIdsSet.add(jobRefId);
    return this.jobGroupService.moveJobFileToGroup(orgId, Array.from(jobRefIdsSet), newJobGroupId != 'default' ? newJobGroupId : null)
      .pipe(
        mergeMap(() => dispatch([
            new FetchJobFiles(pageNumber),
            new SetNotificationAction('Job File/s Moved Successfully', AlertDialogType.SUCCESS)
          ])),
        map(_ => patchState({
            tickedJobFiles: {}
          })),
        catchError((_) => dispatch([new SetNotificationAction('Error Moving job File To New Group', AlertDialogType.ERROR)]))
      );
  }

  @Action(TickJobFile)
  tickJobFile({getState, patchState}: StateContext<JobGroupStateModel>, {jobFile, ticked}: TickJobFile) {
    const { tickedJobFiles } = getState();
    if (!ticked) {
      const newTickedJobFiles = {...tickedJobFiles};
      delete newTickedJobFiles[jobFile.jobRefId];
      return patchState({
        tickedJobFiles: newTickedJobFiles
      });
    } else {
      return patchState({
        tickedJobFiles: {
          ...tickedJobFiles,
          [jobFile.jobRefId]: jobFile
        }
      });
    }
  }

  @Action(GetWorkOrderCompletionPercentage)
  getWorkOrderCompletionPercentage({patchState, dispatch}:
    StateContext<JobGroupStateModel>, {orgId, jobRefId, containerIds}: GetWorkOrderCompletionPercentage) {
    return this.jobMasterService.getPercentageOfContainer(orgId, jobRefId, containerIds).pipe(
      tap((percentage: EntityMap<string, number>) => {
        patchState({
          loadPercentage: percentage
        });
      })
    );
  }

  @Action(SetJobGroupSearchKey)
  setJobGroupSearchKey(
    { patchState, dispatch, getState }: StateContext<JobGroupStateModel>,
    { searchKey }: SetJobGroupSearchKey
  ) {
    patchState({ searchKey });

    const { jobGroupPaginationData, jobFilePaginationData } = getState();
    const jobGroupPageNumber = jobGroupPaginationData.pageNumber;
    const jobFilePageNumber = jobFilePaginationData.pageNumber;

    return this.jobGroupService.getJobGroups(
      this.store.selectSnapshot(state => state.auth.user.orgId),
      jobGroupPageNumber,
      jobGroupPaginationData.pageSize,
      searchKey
    ).pipe(
      map(res => {
        patchState({
          jobGroupPaginationData: {
            pageNumber: res.pageable.pageNumber,
            pageSize: res.pageable.pageSize,
            totalElements: res.totalElements
          },
          jobGroups: res.content.reduce((prev, curr) => {
            prev[curr.id != null ? curr.id : 'default'] = curr;
            return prev;
          }, {})
        });

        const selectedJobGroupId = this.store.selectSnapshot(JobGroupState.getSelectedJobGroupId);
        return selectedJobGroupId;
      }),
      switchMap(selectedGroupId => {
        const groupIdForQuery = searchKey && searchKey !== 'empty'
          ? null
          : (selectedGroupId === 'default' ? null : selectedGroupId);

        return this.jobGroupService.getJobFiles(
          groupIdForQuery,
          jobFilePageNumber,
          jobFilePaginationData.pageSize,
          searchKey
        );
      }),
      map(jobFilesRes => patchState({
          jobFilePaginationData: {
            pageNumber: jobFilesRes.pageable.pageNumber,
            pageSize: jobFilesRes.pageable.pageSize,
            totalElements: jobFilesRes.totalElements
          },
          jobFilesLoading: false,
          jobFiles: jobFilesRes.content.reduce((prev, curr) => {
            prev[curr.jobRefId] = curr;
            return prev;
          }, {})
        })),
      catchError(error => {
        console.error('Error in setJobGroupSearchKey:', error);
        return dispatch(new SetNotificationAction('Error loading data', AlertDialogType.ERROR));
      })
    );
  }

  @Action(SetSelectedJobGroupForDashboardAfterCreation)
  setSelectedJobGroupForDashboardAfterCreation({ patchState }: StateContext<JobGroupStateModel>, { id, jobGroup }: SetSelectedJobGroupForDashboardAfterCreation) {
    patchState({
      selectedJobGroupIdForDashboardAfterCreation: id,
      selectedJobGroupForDashboardAfterCreation: jobGroup
    });
  }

  @Action(DeleteJobGroup)
  deleteJobGroup({ getState, patchState }: StateContext<JobGroupStateModel>, { jobGroupId }: DeleteJobGroup) {
    return this.jobGroupService.deleteJobGroup(jobGroupId).pipe(
      tap(() => {
        const state = getState();
        const jobGroups = {...state.jobGroups};
        const deletedGroup = jobGroups[jobGroupId];
        delete jobGroups[jobGroupId];
        
        patchState({
          jobGroups
        });
        
        (Action as any).jobGroupName = deletedGroup?.name || 'Unknown group';
      }),
      catchError(error => {
        console.error('Error deleting job group:', error);
        return throwError(error);
      })
    );
  }

  @Action(GetJpmIdsByJobRefId)
  getJpmIdsByJobRefId({ dispatch, patchState }: StateContext<JobGroupStateModel>, { orgId, jobRefId, containerId }: GetJpmIdsByJobRefId) {
  
    const {user: {userId}} = this.store.selectSnapshot(authState => authState.auth);
    return this.jobGroupService.getJpms(orgId, jobRefId, userId)
      .pipe(
        tap(jpms => {
          
          patchState({
            jpmIds: jpms
          });
        }),
        catchError(() => dispatch(new SetNotificationAction('Unable to get jpm ids by job ref id', AlertDialogType.ERROR)))
      );
  }
}

